import { SessionCrypto } from './crypto.js';
import { SessionError } from './errors.js';
import { verifyPolicy } from './attestation.js';
import { InferenceStream } from './stream.js';
import { base64Decode, base64Encode, encodeText, getWebSocketConstructor } from './platform.js';
import { defaultAttestationPolicy } from './attestation.js';
import type {
  AttestationPolicy,
  CocoonClientOptions,
  DiscloseField,
  InferenceRequest,
  Model,
  WireModel,
} from './types.js';
import { fromWireModel } from './types.js';

interface SessionResponse {
  type: string;
  session_id?: string;
  public_key?: string;
  attestation_quote?: string;
  proxy_public_key?: string;
  effective_disclose?: string[];
  error?: string;
  code?: string;
}

/**
 * Build the encrypted-body request object from an SDK-shaped InferenceRequest.
 * Performs camelCase to snake_case mapping for fields that need wire renames.
 *
 * Internal passthrough: `tools` and `tool_choice` are forwarded verbatim when
 * present on the input object. They are not part of the public typed API yet
 * — callers reach them via a structural cast — but the wire serializer must
 * propagate them so consumers can opt into the OpenAI-style tools surface
 * without waiting for a typed SDK release.
 *
 * @internal Exported for unit testing; consumers should not call this directly.
 */
export function buildWireReq(req: InferenceRequest): Record<string, unknown> {
  const wireReq: Record<string, unknown> = {
    model: req.model,
    messages: req.messages,
    stream: req.stream ?? true,
  };
  if (req.maxTokens !== undefined) {
    wireReq.max_tokens = req.maxTokens;
  }
  if (req.chatTemplateKwargs !== undefined) {
    const kwargs: Record<string, unknown> = {};
    if (req.chatTemplateKwargs.enableThinking !== undefined) {
      kwargs.enable_thinking = req.chatTemplateKwargs.enableThinking;
    }
    if (Object.keys(kwargs).length > 0) {
      wireReq.chat_template_kwargs = kwargs;
    }
  }
  // Internal passthrough for OpenAI-style fields not yet part of the typed
  // public API. Read off the input object structurally so the SDK does not
  // commit to a stable surface for these in this release.
  const passthrough = req as unknown as {
    tools?: unknown;
    tool_choice?: unknown;
  };
  if (passthrough.tools !== undefined) {
    wireReq.tools = passthrough.tools;
  }
  if (passthrough.tool_choice !== undefined) {
    wireReq.tool_choice = passthrough.tool_choice;
  }
  return wireReq;
}

/**
 * CocoonClient connects to shroud-go's cocoon proxy endpoints.
 * Provides encrypted inference with TEE attestation verification.
 */
export class CocoonClient {
  private readonly baseURL: string;
  private readonly apiKey?: string;
  private readonly modelsPath: string;
  private readonly streamPath: string;
  private readonly attestationPolicy: AttestationPolicy | null;
  private readonly wsConstructor?: unknown;

  constructor(baseURL: string, options?: CocoonClientOptions) {
    this.baseURL = baseURL.replace(/\/+$/, '');
    this.apiKey = options?.apiKey;
    this.modelsPath = options?.modelsPath ?? '/v1/cocoon/models';
    this.streamPath = options?.streamPath ?? '/v1/cocoon/stream';
    this.attestationPolicy =
      options?.attestationPolicy === null ? null : (options?.attestationPolicy ?? defaultAttestationPolicy());
    this.wsConstructor = options?.WebSocket;
  }

  /** Fetch available models from the cocoon endpoint. */
  async listModels(signal?: AbortSignal): Promise<Model[]> {
    const httpURL = this.httpBaseURL() + this.modelsPath;
    const headers: Record<string, string> = {};
    if (this.apiKey) {
      headers['Authorization'] = `Bearer ${this.apiKey}`;
    }

    const resp = await fetch(httpURL, { headers, signal });
    if (!resp.ok) {
      const body = await resp.text();
      throw new SessionError(`Models request failed (${resp.status}): ${body}`);
    }

    const result = (await resp.json()) as { data: WireModel[] };
    return result.data.map(fromWireModel);
  }

  /** Start an encrypted inference session over WebSocket. */
  async inference(req: InferenceRequest, signal?: AbortSignal): Promise<InferenceStream> {
    const crypto = new SessionCrypto();
    const WS = getWebSocketConstructor(this.wsConstructor);
    const wsURL = this.baseURL + this.streamPath;

    // Create WebSocket — browser API doesn't support custom headers,
    // so we pass the API key via protocol if needed, or rely on the URL.
    // For Node.js ws library, we can pass headers.
    const ws = this.createWebSocket(WS, wsURL);

    // Handle abort signal
    if (signal) {
      signal.addEventListener('abort', () => {
        try { ws.close(); } catch { /* ignore */ }
      }, { once: true });
    }

    await this.waitForOpen(ws);

    // Step 1: Send init message
    const initMsg: Record<string, unknown> = {
      sdk_public_key: base64Encode(crypto.publicKey()),
      model: req.model,
    };
    if (req.disclose && req.disclose.length > 0) {
      initMsg.shroud = { disclose: req.disclose };
    }
    ws.send(JSON.stringify(initMsg));

    // Step 2: Read session response
    const sessionResp = await this.readOneMessage<SessionResponse>(ws);

    if (sessionResp.type === 'error') {
      ws.close();
      throw new SessionError(
        `Session error (${sessionResp.code ?? 'unknown'}): ${sessionResp.error ?? 'unknown'}`,
        sessionResp.code,
      );
    }
    if (sessionResp.type !== 'session') {
      ws.close();
      throw new SessionError(`Unexpected response type: ${sessionResp.type}`);
    }

    // Step 3: Decode server public key
    const serverPubKey = base64Decode(sessionResp.public_key!);

    // Step 3a: Verify attestation
    if (this.attestationPolicy) {
      const quoteBytes = base64Decode(sessionResp.attestation_quote!);
      const proxyPubKey = sessionResp.proxy_public_key
        ? base64Decode(sessionResp.proxy_public_key)
        : null;
      verifyPolicy(quoteBytes, proxyPubKey, this.attestationPolicy);
    }

    // Step 4: ECDH key exchange
    crypto.deriveSharedKey(serverPubKey);

    // Step 5: Encrypt and send inference payload
    const wireReq = buildWireReq(req);

    const payload = encodeText(JSON.stringify(wireReq));
    const { ciphertext, nonce } = crypto.encrypt(payload);

    ws.send(JSON.stringify({
      payload: base64Encode(ciphertext),
      nonce: base64Encode(nonce),
    }));

    const effectiveDisclose: DiscloseField[] =
      (sessionResp.effective_disclose ?? []) as DiscloseField[];

    return new InferenceStream(ws, crypto, serverPubKey, effectiveDisclose);
  }

  private createWebSocket(
    WS: new (url: string, protocols?: string | string[]) => WebSocket,
    url: string,
  ): WebSocket {
    // Try to pass headers for Node.js ws library
    if (this.apiKey) {
      try {
        // Node.js ws supports options as third arg
        const ws = new (WS as unknown as new (
          url: string,
          protocols: undefined,
          options: { headers: Record<string, string> },
        ) => WebSocket)(url, undefined, {
          headers: { Authorization: `Bearer ${this.apiKey}` },
        });
        return ws;
      } catch {
        // Browser WebSocket doesn't support headers — fall through
      }
    }
    return new WS(url);
  }

  private waitForOpen(ws: WebSocket): Promise<void> {
    // If already open
    if (ws.readyState === 1) return Promise.resolve();

    return new Promise<void>((resolve, reject) => {
      const onOpen = () => {
        cleanup();
        resolve();
      };
      const onError = (...args: unknown[]) => {
        cleanup();
        const e = args[0] as { message?: string } | undefined;
        const msg = e?.message ?? 'WebSocket connection failed';
        reject(new SessionError(msg));
      };
      const onClose = () => {
        cleanup();
        reject(new SessionError('WebSocket closed before open'));
      };

      const cleanup = () => {
        if (typeof ws.removeEventListener === 'function') {
          ws.removeEventListener('open', onOpen);
          ws.removeEventListener('error', onError as EventListener);
          ws.removeEventListener('close', onClose);
        }
      };

      if (typeof ws.addEventListener === 'function') {
        ws.addEventListener('open', onOpen, { once: true });
        ws.addEventListener('error', onError as EventListener, { once: true });
        ws.addEventListener('close', onClose, { once: true });
      } else {
        // Node.js ws
        const wsNode = ws as unknown as {
          once(event: string, cb: (...args: unknown[]) => void): void;
          removeListener(event: string, cb: (...args: unknown[]) => void): void;
        };
        wsNode.once('open', onOpen);
        wsNode.once('error', onError);
        wsNode.once('close', onClose);
      }
    });
  }

  private readOneMessage<T>(ws: WebSocket): Promise<T> {
    return new Promise<T>((resolve, reject) => {
      const onMessage = (event: MessageEvent | { data: unknown }) => {
        cleanup();
        try {
          const data = typeof event.data === 'string' ? event.data : String(event.data);
          resolve(JSON.parse(data));
        } catch (e) {
          reject(new SessionError('Failed to parse session response', undefined, { cause: e }));
        }
      };
      const onError = (...args: unknown[]) => {
        cleanup();
        const e = args[0] as { message?: string } | undefined;
        const msg = e?.message ?? 'WebSocket error during session setup';
        reject(new SessionError(msg));
      };
      const onClose = () => {
        cleanup();
        reject(new SessionError('WebSocket closed during session setup'));
      };

      const cleanup = () => {
        if (typeof ws.removeEventListener === 'function') {
          ws.removeEventListener('message', onMessage as EventListener);
          ws.removeEventListener('error', onError as EventListener);
          ws.removeEventListener('close', onClose);
        }
      };

      if (typeof ws.addEventListener === 'function') {
        ws.addEventListener('message', onMessage as EventListener, { once: true });
        ws.addEventListener('error', onError as EventListener, { once: true });
        ws.addEventListener('close', onClose, { once: true });
      } else {
        const wsNode = ws as unknown as {
          once(event: string, cb: (...args: unknown[]) => void): void;
          removeListener(event: string, cb: (...args: unknown[]) => void): void;
        };
        wsNode.once('message', (data: unknown) => onMessage({ data }));
        wsNode.once('error', onError);
        wsNode.once('close', onClose);
      }
    });
  }

  /** Convert ws(s):// to http(s)://. */
  private httpBaseURL(): string {
    return this.baseURL.replace('wss://', 'https://').replace('ws://', 'http://');
  }
}
